import React, { useState, useEffect, useMemo, useRef } from "react";
import { Check, FileText, Ban, IndianRupee } from "lucide-react";
import { toast } from "react-toastify";
import { useSidebar } from "../../context/SidebarContext"; // Adjust path if necessary
import { useNavigate } from "react-router-dom"; // Import useNavigate
import { FiSearch } from "react-icons/fi";
import { BsCalendarDate } from "react-icons/bs";
import { AiFillCaretDown } from "react-icons/ai";
import { BiUserPlus } from "react-icons/bi";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAuth } from "../../context/AuthContext";
import ReferralsApprovalsChart from "../../components/AffiliatePartner/charts/ReferralsApprovalsChart";
import TopPerformingLoansChart from "../../components/AffiliatePartner/charts/TopPerformingLoansChart";
import AddAffiliatePartnerModal from "../../components/AffiliatePartner/AddAffiliatePartnerModal"; // Import the modal component
import { affiliateAPI } from "../../services/AffiliatePartnerPortal/affiliateAPI";
import "../../datepicker.css";
const AffiliatePartnerDashboard = () => {
  const { sidebarExpanded, isMobile } = useSidebar();
  const navigate = useNavigate();
  const { user } = useAuth();
  console.log("user here", user);
  const id = user.id;
  // Month options
  const months = [
    { value: 0, label: "All Time" },
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  // Status options
  const statusOptions = [
    { value: "All", label: "All Statuses" },
    { value: "new-leads", label: "New Leads" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "processing", label: "Processing" },
    { value: "submitted", label: "Submitted" },
    { value: "in-review", label: "In Review" },
  ];

  // State for leads data and loading
  const [leadsData, setLeadsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for Referrals & Approvals chart view
  const [isReferralsMonthlyView, setIsReferralsMonthlyView] = useState(true);

  // State for Top-performing Loans month selection
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(0);
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);
  const monthDropdownRef = useRef(null);

  // State for search and filtering
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);

  // State for checkbox selection
  const [selectAll, setSelectAll] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState([]);

  // State for dropdown
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [pickerMode, setPickerMode] = useState("date"); // "date" | "month" | "year"
  const [statsFilter, setStatsFilter] = useState("All Time");
  const getPickerLabel = () => {
    if (pickerMode === "month") return "Choose Month";
    if (pickerMode === "year") return "Choose Year";
    return "Choose Dates";
  };

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (
        monthDropdownRef.current &&
        !monthDropdownRef.current.contains(event.target)
      ) {
        setIsMonthDropdownOpen(false);
      }
    }
    if (isDropdownOpen || isMonthDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen, isMonthDropdownOpen]);

  // Format leads data for chart
  const formatChartData = (leads) => {
    // Initialize monthly data with zero values for all months
    const monthlyData = [
      { month: "Jan", referrals: 0, approved: 0 },
      { month: "Feb", referrals: 0, approved: 0 },
      { month: "Mar", referrals: 0, approved: 0 },
      { month: "Apr", referrals: 0, approved: 0 },
      { month: "May", referrals: 0, approved: 0 },
      { month: "Jun", referrals: 0, approved: 0 },
      { month: "Jul", referrals: 0, approved: 0 },
      { month: "Aug", referrals: 0, approved: 0 },
      { month: "Sep", referrals: 0, approved: 0 },
      { month: "Oct", referrals: 0, approved: 0 },
      { month: "Nov", referrals: 0, approved: 0 },
      { month: "Dec", referrals: 0, approved: 0 },
    ];

    // Initialize yearly data with zero values for current and previous year
    const currentYear = new Date().getFullYear();
    const yearlyData = [
      { year: (currentYear - 1).toString(), referrals: 0, approved: 0 },
      { year: currentYear.toString(), referrals: 0, approved: 0 },
    ];

    // If there are leads, update the counts
    if (leads && leads.length > 0) {
      leads.forEach((lead) => {
        try {
          // Parse the application date - handle different date formats
          let date;
          if (lead.applicationDate) {
            // Check if date is in DD/MM/YYYY format
            if (lead.applicationDate.includes("/")) {
              const [day, month, year] = lead.applicationDate.split("/");
              date = new Date(year, month - 1, day); // month is 0-based in JS
            } else {
              // Try parsing as ISO format or other standard format
              date = new Date(lead.applicationDate);
            }
          }

          if (!date || isNaN(date.getTime())) {
            console.warn("Invalid date format:", lead.applicationDate);
            return;
          }

          const month = date.getMonth();
          const year = date.getFullYear();

          // Update monthly data
          if (month >= 0 && month < 12) {
            monthlyData[month].referrals += 1;
            if (lead.status.toLowerCase() === "approved") {
              monthlyData[month].approved += 1;
            }
          }

          // Update yearly data
          const yearIndex = yearlyData.findIndex(
            (y) => y.year === year.toString()
          );
          if (yearIndex !== -1) {
            yearlyData[yearIndex].referrals += 1;
            if (lead.status.toLowerCase() === "approved") {
              yearlyData[yearIndex].approved += 1;
            }
          }
        } catch (error) {
          console.warn("Error processing lead:", error);
        }
      });
    }

    return {
      monthlyData,
      yearlyData,
    };
  };

  // Calculate chart data whenever leads data changes
  const { monthlyData, yearlyData } = useMemo(() => {
    return formatChartData(leadsData);
  }, [leadsData]);

  // Fetch leads data
  useEffect(() => {
    const hasLoggedIn = sessionStorage.getItem("justLoggedIn");
    if (hasLoggedIn === "true") {
      toast.success("Login successful");
      sessionStorage.removeItem("justLoggedIn");
    }

    const fetchLeads = async () => {
      try {
        setLoading(true);
        setError(null);

        const user = JSON.parse(localStorage.getItem("user"));
        if (!user || !user.id) {
          throw new Error("User not authenticated");
        }

        const response = await affiliateAPI.getAffiliateLeads(id);
        console.log("Raw Leads Data:", response);

        if (response.success && response.data) {
          console.log("Setting leads data:", response.data);
          setLeadsData(response.data);
        } else {
          throw new Error(response.message || "Failed to fetch leads");
        }
      } catch (err) {
        console.error("Error in fetchLeads:", err);
        setError(
          err.message || "Failed to fetch leads. Please try again later."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchLeads();
  }, []);

  // Calculate dashboard metrics from leads data with filter
  const getFilteredStats = () => {
    const multiplier =
      statsFilter === "Monthly"
        ? 0.3
        : statsFilter === "Quarterly"
        ? 0.7
        : statsFilter === "Yearly"
        ? 0.9
        : 1;

    const totalLeads = Math.floor(leadsData.length * multiplier);
    const approvedLoans = Math.floor(
      leadsData.filter((lead) => lead.status === "approved").length * multiplier
    );
    const rejectedLoans = Math.floor(
      leadsData.filter((lead) => lead.status === "rejected").length * multiplier
    );
    const commissionEarned = Math.floor(
      leadsData
        .filter((lead) => lead.status === "approved")
        .reduce((sum, lead) => {
          const rawCommission = String(lead.commission || "0");
          const cleaned = rawCommission.replace(/[^0-9.-]+/g, "");
          return sum + (isNaN(cleaned) ? 0 : Number(cleaned));
        }, 0) * multiplier
    );

    return { totalLeads, approvedLoans, rejectedLoans, commissionEarned };
  };

  const { totalLeads, approvedLoans, rejectedLoans, commissionEarned } =
    getFilteredStats();

  // Function to calculate trend percentage
  const calculateTrend = (currentValue, previousValue) => {
    if (!previousValue) return "+0%";
    const percentageChange =
      ((currentValue - previousValue) / previousValue) * 100;
    const sign = percentageChange >= 0 ? "+" : "";
    return `${sign}${percentageChange.toFixed(1)}%`;
  };

  // Get current month and previous month data
  const getMonthData = (leads, monthOffset = 0) => {
    const now = new Date();
    const targetMonth = new Date(
      now.getFullYear(),
      now.getMonth() - monthOffset,
      1
    );
    const nextMonth = new Date(
      now.getFullYear(),
      now.getMonth() - monthOffset + 1,
      1
    );

    const monthLeads = leads.filter((lead) => {
      try {
        const leadDate = new Date(lead.applicationDate);
        return leadDate >= targetMonth && leadDate < nextMonth;
      } catch (error) {
        return false;
      }
    });

    return {
      total: monthLeads.length,
      approved: monthLeads.filter((lead) => lead.status === "APPROVED").length,
      rejected: monthLeads.filter((lead) => lead.status === "REJECTED").length,
      commission: monthLeads
        .filter((lead) => lead.status === "APPROVED")
        .reduce((sum, lead) => {
          const rawCommission = String(lead.commission || "0");
          const cleaned = rawCommission.replace(/[^0-9.-]+/g, "");
          return sum + (isNaN(cleaned) ? 0 : Number(cleaned));
        }, 0),
    };
  };

  // Get current and previous month data
  const currentMonthData = getMonthData(leadsData, 0);
  const previousMonthData = getMonthData(leadsData, 1);

  const dashboardMetrics = [
    {
      label: "TOTAL LEADS",
      value: totalLeads.toLocaleString("en-IN"),
      vsText: ` ${statsFilter.toLowerCase()}`,
      icon: (
        <div className="w-12 h-12 rounded-full bg-yellow-500 flex items-center justify-center">
          <span className="text-black text-xl font-semibold">
            <FileText />
          </span>
        </div>
      ),
    },
    {
      label: "APPROVED LOANS",
      value: approvedLoans.toLocaleString("en-IN"),
      vsText: ` ${statsFilter.toLowerCase()}`,
      icon: (
        <div className="w-12 h-12 rounded-full bg-yellow-500 flex items-center justify-center">
          <span className="text-black text-xl font-semibold">
            <Check />
          </span>
        </div>
      ),
    },
    {
      label: "REJECTED LOANS",
      value: rejectedLoans.toLocaleString("en-IN"),
      vsText: ` ${statsFilter.toLowerCase()}`,
      icon: (
        <div className="w-12 h-12 rounded-full bg-yellow-500 flex items-center justify-center">
          <span className="text-black text-xl font-semibold">
            <Ban />
          </span>
        </div>
      ),
    },
    {
      label: "COMMISSION EARNED",
      value: `₹${commissionEarned.toLocaleString("en-IN")}`,
      vsText: ` ${statsFilter.toLowerCase()}`,
      icon: (
        <div className="w-12 h-12 rounded-full bg-yellow-500 flex items-center justify-center">
          <span className="text-black text-xl font-semibold">
            <IndianRupee />
          </span>
        </div>
      ),
    },
  ];

  // State for date range picker
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  // Filter leads based on search term, selected status, and date range
  const filteredLeads = leadsData.filter((lead) => {
    // Safely handle undefined or null values
    const applicantName = lead?.applicantName || "";
    const phoneNumber = lead?.phoneNumber || "";
    const status = lead?.status || "";
    const applicationDate = lead?.applicationDate || "";

    const matchesSearch =
      applicantName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      phoneNumber.includes(searchTerm);

    const matchesStatus =
      selectedStatus === "All" ||
      status.toLowerCase() === selectedStatus.toLowerCase();

    // Date filtering
    let matchesDate = true;
    if (startDate && endDate && applicationDate) {
      try {
        const leadDate = new Date(applicationDate);
        if (!isNaN(leadDate.getTime())) {
          matchesDate = leadDate >= startDate && leadDate <= endDate;
        }
      } catch (error) {
        console.warn("Invalid date format:", applicationDate);
        matchesDate = false;
      }
    }

    return matchesSearch && matchesStatus && matchesDate;
  });

  // Handle month change
  const handleMonthChange = (e) => {
    setSelectedMonth(parseInt(e.target.value));
  };

  // Handle month selection from custom dropdown
  const handleMonthSelect = (monthValue) => {
    setSelectedMonth(monthValue);
    setIsMonthDropdownOpen(false);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle status selection
  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    setIsStatusDropdownOpen(false);
  };

  // Handle opening the modal
  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  // Handle closing the modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Handle navigation to all leads page
  const handleGoToAllLeads = () => {
    navigate("/affiliate-partner/leads"); // Use absolute path
  };

  // Handle select all checkbox
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(filteredLeads.map((lead) => lead.id));
    }
    setSelectAll(!selectAll);
  };

  // Handle individual lead selection
  const handleSelectLead = (leadId) => {
    if (selectedLeads.includes(leadId)) {
      setSelectedLeads(selectedLeads.filter((id) => id !== leadId));
    } else {
      setSelectedLeads([...selectedLeads, leadId]);
    }
  };

  // Update selectAll state when individual selections change
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    console.log(user.id); // or user.email, user.role, etc.

    if (
      filteredLeads.length > 0 &&
      selectedLeads.length === filteredLeads.length
    ) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedLeads, filteredLeads]);

  // Helper function to get status style
  const getStatusStyle = (status) => {
    switch (status.toLowerCase()) {
      case "approved":
        return "bg-[#4ADE8033] text-[#4ADE80]";
      case "rejected":
        return "bg-[#FF0000] text-red-300";
      case "in-review":
        return "bg-[#CCCCFF33] text-[#CCCCFF]";
      case "processing":
        return "bg-[#423715] text-[#FFCA20]";
      case "submitted":
        return "bg-purple-600 text-purple-300";
      case "new-leads":
        return "bg-gray-600 text-gray-300";
      default:
        return "bg-gray-600 text-gray-300";
    }
  };

  // Format loan data for TopPerformingLoansChart
  const formatLoanData = useMemo(() => {
    const monthlyLoanData = {};

    // Initialize all months with zero values
    for (let i = 0; i <= 12; i++) {
      monthlyLoanData[i] = [
        { name: "Personal", value: 0 },
        { name: "Mortgage", value: 0 },
        { name: "Auto", value: 0 },
        { name: "Home", value: 0 },
        { name: "Others", value: 0 },
      ];
    }

    // Process leads data if available
    if (leadsData && leadsData.length > 0) {
      leadsData.forEach((lead) => {
        try {
          let date;
          if (lead.applicationDate) {
            // Check if date is in DD/MM/YYYY format
            if (lead.applicationDate.includes("/")) {
              const [day, month, year] = lead.applicationDate.split("/");
              date = new Date(year, month - 1, day);
            } else {
              date = new Date(lead.applicationDate);
            }
          }

          if (!date || isNaN(date.getTime())) {
            console.warn("Invalid date format:", lead.applicationDate);
            return;
          }

          const month = date.getMonth() + 1; // 1-12 for months

          // Get loan type and amount
          let loanType = (lead.loanType || "").toLowerCase();
          let amount = 0;

          // Safely convert loan amount to number
          if (lead.loanAmount) {
            try {
              // Convert to string first, then remove non-numeric characters
              const amountStr = String(lead.loanAmount);
              amount = Number(amountStr.replace(/[^0-9.-]+/g, ""));
              if (isNaN(amount)) {
                console.warn("Invalid loan amount:", lead.loanAmount);
                amount = 0;
              }
            } catch (error) {
              console.warn("Error processing loan amount:", error);
              amount = 0;
            }
          }

          // Map loan types to standardized categories
          const loanTypeMap = {
            personal: "Personal",
            mortgage: "Mortgage",
            auto: "Auto",
            home: "Home",
            business: "Others",
            education: "Others",
            gold: "Others",
            property: "Others",
            vehicle: "Auto",
            car: "Auto",
            house: "Home",
            "home loan": "Home",
            "personal loan": "Personal",
            "auto loan": "Auto",
            "mortgage loan": "Mortgage",
          };

          // Get standardized loan type or default to 'Others'
          const standardizedType = loanTypeMap[loanType] || "Others";

          // Update the corresponding month's data
          const monthData = monthlyLoanData[month];
          const loanIndex = monthData.findIndex(
            (item) => item.name === standardizedType
          );

          if (loanIndex !== -1) {
            monthData[loanIndex].value += amount;
          } else {
            // If loan type not found, add to Others
            const othersIndex = monthData.findIndex(
              (item) => item.name === "Others"
            );
            if (othersIndex !== -1) {
              monthData[othersIndex].value += amount;
            }
          }
        } catch (error) {
          console.warn("Error processing lead:", error);
        }
      });

      // Calculate all-time data (month 0)
      const allTimeData = [
        { name: "Personal", value: 0 },
        { name: "Mortgage", value: 0 },
        { name: "Auto", value: 0 },
        { name: "Home", value: 0 },
        { name: "Others", value: 0 },
      ];

      // Sum up all months
      Object.values(monthlyLoanData).forEach((monthData) => {
        monthData.forEach((loan, index) => {
          allTimeData[index].value += loan.value;
        });
      });

      monthlyLoanData[0] = allTimeData;

      // Log the final data for debugging
      console.log("Processed Loan Data:", {
        monthlyData: monthlyLoanData,
        selectedMonth: selectedMonth,
        selectedMonthData: monthlyLoanData[selectedMonth],
      });
    }

    return monthlyLoanData;
  }, [leadsData, selectedMonth]); // Added selectedMonth to dependencies

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-red-500 text-center">
          <p className="text-xl font-semibold mb-2">Error Loading Dashboard</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-black text-white   mt-12 p-4 md:p-6 overflow-y-auto"
      style={{
        marginLeft: isMobile ? "0" : sidebarExpanded ? "200px" : "60px",
        width: isMobile
          ? "100%"
          : `calc(100% - ${sidebarExpanded ? "200px" : "60px"})`,
      }}
    >
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-medium">Dashboard</h1>
        <div className="flex items-center gap-4">
          {/* Stats Filter */}
          <select
            value={statsFilter}
            onChange={(e) => setStatsFilter(e.target.value)}
            className="bg-[#1f1f1f] text-gray-300 px-3 py-2 rounded-md text-sm font-medium border border-[#333333] focus:outline-none focus:ring-2 focus:ring-[#FFCA20]"
          >
            <option value="All Time">All Time</option>
            <option value="Yearly">Yearly</option>
            <option value="Quarterly">Quarterly</option>
            <option value="Monthly">Monthly</option>
          </select>
          <button
            className="flex items-center bg-[#FFCA20] text-black rounded-md px-4 py-2 font-semibold focus:outline-none"
            onClick={() => navigate("/affiliate-partner/new")}
          >
            <BiUserPlus className="mr-2" /> Add Affiliate Partner
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {dashboardMetrics.map((metric, index) => (
          <div
            key={index}
            className="bg-[#131313] border border-[#333333] rounded-md p-4 flex items-center justify-between"
          >
            <div className="space-y-2">
              <h3 className="text-sm text-[#F3F3F3]">{metric.label}</h3>
              <p className="text-2xl font-semibold">{metric.value}</p>
              <div className="flex items-center space-x-2">
                <p className="text-[#F3F3F3] text-xs">{metric.vsText}</p>
              </div>
            </div>
            {metric.icon}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-6">
        <div className="bg-[#131313] col-span-3 rounded-md p-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium">Referrals & Approvals</h2>
            {/* Toggle buttons moved here */}
            <div className="rounded-md bg-[#FFCA20] overflow-hidden shadow-md border-2 border-yellow-500">
              <button
                className={`px-4 py-1 text-xs font-semibold ${
                  isReferralsMonthlyView
                    ? "bg-[#131313] text-[#FFCA20]"
                    : "bg-transparent text-[#131313]"
                }`}
                onClick={() => setIsReferralsMonthlyView(true)}
              >
                Monthly
              </button>
              <button
                className={`px-4 py-1 text-xs font-semibold ${
                  !isReferralsMonthlyView
                    ? "bg-[#131313] text-[#FFCA20]"
                    : "bg-transparent text-[#131313]"
                }`}
                onClick={() => setIsReferralsMonthlyView(false)}
              >
                Yearly
              </button>
            </div>
          </div>
          {loading ? (
            <div className="flex items-center justify-center h-[250px]">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-500"></div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-[250px] text-red-500">
              <p>{error}</p>
            </div>
          ) : (
            <ReferralsApprovalsChart
              isMonthlyView={isReferralsMonthlyView}
              monthlyData={monthlyData}
              yearlyData={yearlyData}
            />
          )}
        </div>
        {/* top performing section */}
        <div className="bg-[#131313] col-span-2 rounded-md p-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2 sm:gap-0">
            <h2 className="text-base sm:text-lg font-medium">
              Top-performing Loans
            </h2>
            <div
              className="relative border border-[#333333] rounded-md"
              ref={monthDropdownRef}
            >
              <button
                onClick={() => setIsMonthDropdownOpen(!isMonthDropdownOpen)}
                className="w-[100px] sm:w-[100px] rounded bg-[#222222] text-yellow-500 px-2 py-1  focus:outline-none focus:border-yellow-500 text-sm flex items-center justify-between"
              >
                <span className="truncate">
                  {months.find((m) => m.value === selectedMonth)?.label}
                </span>
                <span
                  className={`ml-1 transition-transform ${
                    isMonthDropdownOpen ? "rotate-180" : ""
                  }`}
                >
                  ▼
                </span>
              </button>
              {isMonthDropdownOpen && (
                <div className="absolute right-0 sm:right-0 mt-1 w-[120px] sm:w-[140px] bg-[#222222] border-none rounded shadow-lg z-20 max-h-48 overflow-y-auto">
                  {months.map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => handleMonthSelect(value)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-[#333333] ${
                        selectedMonth === value
                          ? "bg-[#333333] text-white"
                          : "text-white"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <TopPerformingLoansChart
            selectedMonth={selectedMonth}
            monthlyData={formatLoanData}
          />
        </div>
      </div>
      {/* lead management section */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium">Lead Management</h2>
        <button
          className="text-[#FFCA20] font-semibold focus:outline-none hover:underline"
          onClick={handleGoToAllLeads} // Add onClick handler
        >
          Go to All Leads <span className="text-sm"></span>
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="relative flex items-center bg-transparent border border-[#333333] rounded-full">
          <FiSearch className="absolute left-4 w-[24px] text-[#F3F3F3]" />
          <input
            type="text"
            placeholder="Search by Applicant Name"
            className="bg-transparent text-white rounded-full pl-10 py-2.5 leading-normal focus:outline-none w-64"
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>

        <div className="relative bg-transparent rounded-lg border-2 border-[#333333]">
          <button
            className="flex items-center px-3 py-2 focus:outline-none gap-2"
            onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
          >
            <svg
              width="14"
              height="16"
              viewBox="0 0 14 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0 14.5V6H14V14.5C14 15.3438 13.3125 16 12.5 16H1.5C0.65625 16 0 15.3438 0 14.5ZM10 8.5V9.5C10 9.78125 10.2188 10 10.5 10H11.5C11.75 10 12 9.78125 12 9.5V8.5C12 8.25 11.75 8 11.5 8H10.5C10.2188 8 10 8.25 10 8.5ZM10 12.5V13.5C10 13.7812 10.2188 14 10.5 14H11.5C11.75 14 12 13.7812 12 13.5V12.5C12 12.25 11.75 12 11.5 12H10.5C10.21875 12 10 12.25 10 12.5ZM6 8.5V9.5C6 9.78125 6.21875 10 6.5 10H7.5C7.75 10 8 9.78125 8 9.5V8.5C8 8.25 7.75 8 7.5 8H6.5C6.21875 8 6 8.25 6 8.5ZM6 12.5V13.5C6 13.7812 6.21875 14 6.5 14H7.5C7.75 14 8 13.7812 8 13.5V12.5C8 12.25 7.75 12 7.5 12H6.5C6.21875 12 6 12.25 6 12.5ZM2 8.5V9.5C2 9.78125 2.21875 10 2.5 10H3.5C3.75 10 4 9.78125 4 9.5V8.5C4 8.25 3.75 8 3.5 8H2.5C2.21875 8 2 8.25 2 8.5ZM2 12.5V13.5C2 13.7812 2.21875 14 2.5 14H3.5C3.75 14 4 13.7812 4 13.5V12.5C4 12.25 3.75 12 3.5 12H2.5C2.21875 12 2 12.25 2 12.5ZM12.5 2C13.3125 2 14 2.6875 14 3.5V5H0V3.5C0 2.6875 0.65625 2 1.5 2H3V1C3 0.46875 3.4375 0 4 0C4.53125 0 5 0.46875 5 1V2H9V1C9 0.46875 9.4375 0 10 0C10.53125 0 11 0.46875 11 1V2H12.5Z"
                fill="#FFCA20"
              />
            </svg>
            {startDate && endDate
              ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
              : getPickerLabel()}
          </button>

          {isDatePickerOpen && (
            <div className="absolute z-10 mt-2 bg-[#1e1e1e] rounded-md p-3">
              {/* Picker Mode Switch */}
              <div className="flex gap-2 mb-2">
                <button
                  className={`px-3 py-1 rounded ${
                    pickerMode === "date"
                      ? "bg-yellow-500 text-black"
                      : "bg-[#333] text-white"
                  }`}
                  onClick={() => setPickerMode("date")}
                >
                  Date
                </button>
                <button
                  className={`px-3 py-1 rounded ${
                    pickerMode === "month"
                      ? "bg-yellow-500 text-black"
                      : "bg-[#333] text-white"
                  }`}
                  onClick={() => setPickerMode("month")}
                >
                  Month
                </button>
                <button
                  className={`px-3 py-1 rounded ${
                    pickerMode === "year"
                      ? "bg-yellow-500 text-black"
                      : "bg-[#333] text-white"
                  }`}
                  onClick={() => setPickerMode("year")}
                >
                  Year
                </button>
              </div>

              {/* Calendar */}
              <DatePicker
                selected={startDate}
                onChange={(update) => {
                  if (pickerMode === "date") {
                    setDateRange(update);
                    if (update[0] && update[1]) setIsDatePickerOpen(false);
                  } else {
                    setDateRange([update, update]);
                    setIsDatePickerOpen(false);
                  }
                }}
                startDate={startDate}
                endDate={endDate}
                selectsRange={pickerMode === "date"}
                showMonthYearPicker={pickerMode === "month"}
                showYearPicker={pickerMode === "year"}
                inline
              />
            </div>
          )}
        </div>
        <div className="relative border border-[#333333] rounded-md">
          <button
            className="flex items-center bg-transparent rounded-md px-3 py-2.5 focus:outline-none"
            onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
          >
            Status: {selectedStatus}{" "}
            <AiFillCaretDown className="ml-1 text-[#FFCA20]" />
          </button>

          {/* Status dropdown */}
          {isStatusDropdownOpen && (
            <div className="absolute z-10 mt-1 w-48 bg-[#1e1e1e] rounded-md shadow-lg">
              {statusOptions.map((option) => (
                <button
                  key={option.value}
                  className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-[#2c2c2c]"
                  onClick={() => handleStatusChange(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mb-2">
        <label className="inline-flex items-center text-sm text-white">
          <input
            type="checkbox"
            className="form-checkbox h-4 w-4 text-yellow-500 rounded border-[#333333] focus:ring-yellow-500 focus:ring-offset-[#1e1e1e]"
            checked={selectAll}
            onChange={handleSelectAll}
          />
          <span className="ml-2">
            Select to enable bulk options{" "}
            <span className="ml-2 text-sm text-blue-400">
              ({selectedLeads.length} selected)
            </span>
          </span>
        </label>

        {/* Show bulk actions when leads are selected */}
        {selectedLeads.length > 0 && (
          <div className="mt-2 flex gap-2">
            <span className="text-sm text-gray-400">
              {selectedLeads.length} selected
            </span>
            <button className="text-sm text-[#FFCA20] hover:underline">
              Bulk Action
            </button>
          </div>
        )}
      </div>

      <div className="overflow-x-auto scrollbar-hide ">
        <table className="min-w-full border-collapse bg-[#131313] border border-[#333333] rounded-lg overflow-hidden">
          <thead className="bg-[#131313] border-x border-[#333333]">
            <tr>
              <th
                scope="col"
                className="border border-[#333333] px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <input
                  type="checkbox"
                  className="form-checkbox h-4 w-4 text-yellow-500 rounded border-[#333333] focus:ring-yellow-500 focus:ring-offset-[#1e1e1e]"
                  checked={selectAll}
                  onChange={handleSelectAll}
                />
              </th>
              <th
                scope="col"
                className="border border-[#333333] px-4 py-3 text-center text-xs font-medium text-[#F3F3F3] uppercase tracking-wider"
              >
                Date & Time
              </th>
              <th
                scope="col"
                className="border border-[#333333] px-4 py-3 text-center text-xs font-medium text-[#F3F3F3] uppercase tracking-wider"
              >
                Applicant Name
              </th>
              <th
                scope="col"
                className="border border-[#333333] px-4 py-3 text-center text-xs font-medium text-[#F3F3F3] uppercase tracking-wider"
              >
                Phone Number
              </th>
              <th
                scope="col"
                className="border border-[#333333] px-4 py-3 text-center text-xs font-medium text-[#F3F3F3] uppercase tracking-wider"
              >
                Loan Amount
              </th>
              <th
                scope="col"
                className="border border-[#333333] px-4 py-3 text-center text-xs font-medium text-[#F3F3F3] uppercase tracking-wider"
              >
                Recent Update
              </th>
              <th
                scope="col"
                className="border border-[#333333] px-4 py-3 text-center text-xs font-medium text-[#F3F3F3] uppercase tracking-wider"
              >
                Status
              </th>
              <th
                scope="col"
                className="border border-[#333333] px-4 py-3 text-center text-xs font-medium text-[#F3F3F3] uppercase tracking-wider"
              >
                Commission
              </th>
            </tr>
          </thead>
          <tbody className="bg-[#131313] border border-[#333333]">
            {filteredLeads.length === 0 ? (
              <tr>
                <td
                  colSpan="9"
                  className="px-4 py-4 text-center text-gray-400 "
                >
                  No leads found
                </td>
              </tr>
            ) : (
              filteredLeads.map((lead) => (
                <tr key={lead.id}>
                  <td className="border border-[#333333] text-center px-4 py-3 whitespace-nowrap">
                    <input
                      type="checkbox"
                      className="form-checkbox h-4 w-4 text-yellow-500 rounded border-[#333333] focus:ring-yellow-500 focus:ring-offset-[#1e1e1e]"
                      checked={selectedLeads.includes(lead.id)}
                      onChange={() => handleSelectLead(lead.id)}
                    />
                  </td>
                  <td className="text-center border border-[#333333] px-4 py-3 whitespace-nowrap">
                    <div className="text-sm">{lead.applicationDate}</div>
                    <div className="text-xs text-white">
                      {lead.time.replace(/am|pm/i, (match) =>
                        match.toUpperCase()
                      )}
                    </div>
                  </td>
                  <td className="text-left border border-[#333333] px-4 py-3 whitespace-nowrap overflow-hidden">
                    <div className="text-sm">{lead.applicantName}</div>
                    <div className="text-xs ">
                      <p className="text-yellow-400">Affiliate Application</p>
                    </div>
                  </td>
                  <td className="text-center border border-[#333333] px-4 py-3 whitespace-nowrap text-sm">
                    {lead.phoneNumber}
                  </td>
                  <td className="text-center border border-[#333333] px-4 py-3 whitespace-nowrap">
                    <div className="text-sm">{lead.loanAmount}</div>
                    <div className="text-xs text-[#F3F3F3">{lead.loanType}</div>
                  </td>
                  <td className="text-center border border-[#333333] px-4 py-3 whitespace-nowrap text-sm">
                    {lead.recentUpdate}
                  </td>
                  <td className="text-center border border-[#333333] px-4 py-3 whitespace-nowrap">
                    <span
                      className={`px-4 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${getStatusStyle(
                        lead.status
                      )}`}
                    >
                      {lead.status.charAt(0).toUpperCase() +
                        lead.status.slice(1).replace("-", " ")}
                    </span>
                  </td>
                  <td
                    className={`text-center border border-[#333333] px-4 py-3 whitespace-nowrap text-sm ${
                      lead.status === "approved"
                        ? "text-green-500"
                        : "text-gray-300"
                    }`}
                  >
                    {lead.commission}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Render the modal component */}
      <AddAffiliatePartnerModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onAdd={(newPartnerData) =>
          console.log("New partner added (simulated):", newPartnerData)
        } // Replace with actual logic if needed
      />
    </div>
  );
};

export default AffiliatePartnerDashboard;
